// #373 — what the "Schedule again" dialog shows for messages that carry media: one entry per message with
// an asset, so the user can see which attachments the new copies will send. Pure, no React.
import type { UserMessage } from '@bt-common/schemas/bt/user-message';

export type ScheduleAgainMediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'file';

export interface ScheduleAgainMediaItem {
    id: string;
    recipient: string;
    kind: ScheduleAgainMediaKind;
    /** Display filename when the asset has one. */
    name: string;
    url: string;
    /** What MessageAssetPreview switches on: the asset's MIME type, or one derived from the kind when the
     *  asset has none (the engine detects it from the URL at send time). */
    previewType: string;
}

const PREVIEW_TYPE_BY_KIND: Record<ScheduleAgainMediaKind, string> = {
    image: 'image/*', video: 'video/*', audio: 'audio/*', pdf: 'application/pdf', file: 'application/octet-stream',
};

/** MessageAssetPreview only draws image/video/audio/application; anything else (text/plain, text/csv) would
 *  render a blank tile, so it gets the generic file icon via the application type. */
export function previewTypeOf(type: string | null | undefined, kind: ScheduleAgainMediaKind): string {
    // MessageAssetPreview matches with a case-sensitive includes(), so normalise before testing and returning.
    const t = (type ?? '').toLowerCase();
    return /image|video|audio|application/.test(t) ? t : PREVIEW_TYPE_BY_KIND[kind];
}

export function mediaKindOf(type: string | null | undefined, name?: string | null): ScheduleAgainMediaKind {
    const t = (type ?? '').toLowerCase();
    if (t.includes('image')) return 'image';
    if (t.includes('video')) return 'video';
    if (t.includes('audio')) return 'audio';
    if (t.includes('pdf') || /\.pdf$/i.test(name ?? '')) return 'pdf';
    return 'file';
}

/** Messages with a real asset (`asset: {url: ''}` on a text message is not media). */
export function scheduleAgainMedia(messages: UserMessage[]): ScheduleAgainMediaItem[] {
    const out: ScheduleAgainMediaItem[] = [];
    for (const m of messages) {
        const asset = m.asset;
        if (!asset?.url) continue;
        const contact = m.contactList?.[0];
        const kind = mediaKindOf(asset.type, asset.name);
        out.push({
            id: m._id,
            recipient: contact?.name || contact?.id || '',
            kind,
            name: asset.name ?? '',
            url: asset.url,
            previewType: previewTypeOf(asset.type, kind),
        });
    }
    return out;
}
